import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { isEbattle } from 'app/config/videogameSpecific.config.helper';
import { getStatWinner } from 'app/utils/entities/match_data';
import { isNumber } from 'app/utils/types';
import MatchesRecap from 'app/components/MatchesRecap';
import SlidingContent from 'app/components/SlidingContent';
import TeamName from 'app/components/TeamName';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';

import FormEncounter from './FormEncounter';
import { getFormWinrate } from './utils';
import style from './style.module.scss';

interface Props {
  opponents: OpponentWithForm[] | undefined;
}

const FormGuideBlock: FC<Props> = ({ opponents }) => {
  const { t } = useTranslation();

  const opponent1 = opponents?.at(0);
  const opponent2 = opponents?.at(1);

  const [selectedEncounter, setSelectedEncounter] = useState<null | Encounter>(null);

  const opponent1HasForm = Boolean(opponent1?.form && opponent1.form.length);
  const opponent2HasForm = Boolean(opponent2?.form && opponent2.form.length);

  const opponent1Winrate = opponent1 ? getFormWinrate(opponent1.id, opponent1.form) : null;
  const opponent2Winrate = opponent2 ? getFormWinrate(opponent2.id, opponent2.form) : null;

  const winrateWinner = getStatWinner({
    highlighted: 'max',
    team1Value: opponent1Winrate,
    team2Value: opponent2Winrate
  });

  const hasSelectedEncounter = Boolean(selectedEncounter);

  const formSlug = opponent1?.form?.at(0)?.videogame_slug ?? opponent2?.form?.at(0)?.videogame_slug;
  const isEBattle = formSlug ? isEbattle(formSlug) : false;

  return (
    <BlockTemplate
      title={t('blocks_form-guide_title')}
      backButtonCallback={hasSelectedEncounter ? () => setSelectedEncounter(null) : undefined}
      hasData={opponent1HasForm || opponent2HasForm}
    >
      <SlidingContent.Container sliding={hasSelectedEncounter}>
        <SlidingContent aria-hidden={hasSelectedEncounter}>
          <div className={style.formGuideBlockContent}>
            {isEBattle ? (
              <div className={style.ebattleTeamsNames}>
                <div className={style.ebattleTeamColumn}>
                  <TeamName team={opponent1} logoStyle={style.hiddenLogo} />
                  <div
                    className={classNames(style.winrate, {
                      [style.lowestWinrate]: !winrateWinner[0]
                    })}
                  >
                    {opponent1HasForm && isNumber(opponent1Winrate) ? `${opponent1Winrate}%` : '-'}
                  </div>
                </div>
                <div className={style.ebattleTeamColumn}>
                  <TeamName team={opponent2} logoStyle={style.hiddenLogo} />
                  <div
                    className={classNames(style.winrate, {
                      [style.lowestWinrate]: !winrateWinner[1]
                    })}
                  >
                    {opponent2HasForm && isNumber(opponent2Winrate) ? `${opponent2Winrate}%` : '-'}
                  </div>
                </div>
              </div>
            ) : (
              <TeamName.Container className={style.teamsNames}>
                <TeamName
                  team={opponent1}
                  logoStyle={style.teamNameLogoSize}
                  namePosition="bottom"
                />
                <div className={style.winrates}>
                  <div className={classNames({ [style.lowestWinrate]: !winrateWinner[0] })}>
                    {opponent1HasForm && isNumber(opponent1Winrate) ? `${opponent1Winrate}%` : '-'}
                  </div>
                  <div className={classNames({ [style.lowestWinrate]: !winrateWinner[1] })}>
                    {opponent2HasForm && isNumber(opponent2Winrate) ? `${opponent2Winrate}%` : '-'}
                  </div>
                </div>
                <TeamName
                  team={opponent2}
                  logoStyle={style.teamNameLogoSize}
                  namePosition="bottom"
                  orientation="reversed"
                />
              </TeamName.Container>
            )}

            <div className={style.lines}>
              {opponent1HasForm ? (
                <ul className={style.encountersContainer}>
                  {opponent1?.form?.map(encounter => (
                    <li key={encounter.id} className={style.line}>
                      <FormEncounter
                        encounter={encounter}
                        opponentId={opponent1?.id}
                        onSelect={setSelectedEncounter}
                        selectable={!hasSelectedEncounter}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={classNames(style.encountersContainer, style.noEncounters)}>
                  {t('error_no-data_compact')}
                </div>
              )}
              {opponent2HasForm ? (
                <ul className={style.encountersContainer}>
                  {opponent2?.form?.map(encounter => (
                    <li key={encounter.id} className={style.line}>
                      <FormEncounter
                        key={encounter.id}
                        encounter={encounter}
                        opponentId={opponent2?.id}
                        onSelect={setSelectedEncounter}
                        selectable={!hasSelectedEncounter}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <div className={classNames(style.encountersContainer, style.noEncounters)}>
                  {t('error_no-data_compact')}
                </div>
              )}
            </div>
          </div>
        </SlidingContent>

        <SlidingContent>
          {selectedEncounter &&
            selectedEncounter.games.length &&
            selectedEncounter.opponents.length === 2 && (
              <MatchesRecap
                encounter={selectedEncounter}
                opponent1={selectedEncounter.opponents.at(0)}
                opponent2={selectedEncounter.opponents.at(1)}
              />
            )}
        </SlidingContent>
      </SlidingContent.Container>
    </BlockTemplate>
  );
};

FormGuideBlock.displayName = 'FormGuideBlock';

export default FormGuideBlock;
